/**
 * FeatureAccessNotice — where "who can use this feature?" is answered now (RBAC-6.2, FR-21).
 *
 * Settings → Contracts and Settings → Design each used to carry a role picker writing
 * `tenant_settings.contract_access_role_ids` / `design_access_role_ids`. 20261103x2 retired both
 * columns: who may use a feature is a permission like everything else, chosen per role in
 * Roles & Permissions, and whether the account has the feature at all is a Joules superadmin's switch
 * (Settings → Add-on modules). This card says so and links there, so an admin who comes looking for
 * the old picker is not left with a missing control.
 */
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ROLES_SETTINGS_HREF } from '@/lib/accountRoles';

export function featureAccessCopy(feature: string, permissionLabel: string) {
  return {
    title: `Who can use ${feature}`,
    body:
      `People whose role includes "${permissionLabel}" can use ${feature}. ` +
      'Choose which roles have it in Roles & Permissions.',
    link: 'Open Roles & Permissions',
  } as const;
}

export function FeatureAccessNotice({ feature, permissionLabel }: { feature: string; permissionLabel: string }) {
  const copy = featureAccessCopy(feature, permissionLabel);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {copy.title}
        </CardTitle>
        <CardDescription>{copy.body}</CardDescription>
      </CardHeader>
      <CardContent>
        <Link to={ROLES_SETTINGS_HREF} className="text-sm font-medium text-primary underline-offset-4 hover:underline">
          {copy.link}
        </Link>
      </CardContent>
    </Card>
  );
}
